import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip
} from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';
import { FaFileInvoice, FaSync, FaDownload, FaFileCsv } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useDrawer } from 'components/app/DrawerContext';
import {
  getExpenseLedgerEntries,
  clearExpenseLedgerEntries,
  resetExpenseLedgerPagination
} from 'slices/fundManagementInvestment/fundManagementReportsSlice';
import type { RootState } from 'store';
import UserDetailsModal from '../expenseLedger/UserDetailsModal';
import TransactionTypeBadge from '../expenseLedger/TransactionTypeBadge';
import {
  TRANSACTION_GROUPS,
  getTransactionTypeConfig
} from '../expenseLedger/transactionTypeConfig';

interface ExpenseLedgerProps {
  mode?: 'admin' | 'investor';
}

const ExpenseLedger: React.FC<ExpenseLedgerProps> = ({ mode }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { fundManagementAdminRight } = useDrawer();
  const resolvedMode = mode || (fundManagementAdminRight ? 'admin' : 'investor');
  const isInvestorMode = resolvedMode === 'investor';

  // Redux state with default values to prevent undefined errors
  const {
    expenseLedgerEntries = {},
    expenseLedgerLoading = false,
    expenseLedgerError = null,
    expenseLedgerContinuationToken = null,
    expenseLedgerHasMore = false,
    expenseLedgerTotalCount = 0
  } = useSelector((state: RootState) => state.fundManagementReports || {});

  // Helper function to flatten grouped ledger entries
  const getFlattenedEntries = () => {
    if (!expenseLedgerEntries) return [];
    
    // If it's an array, return it as is
    if (Array.isArray(expenseLedgerEntries)) {
      return expenseLedgerEntries;
    }
    
    // If it's an object with transaction types as keys, flatten it
    if (typeof expenseLedgerEntries === 'object') {
      return Object.values(expenseLedgerEntries).flat().filter(Boolean) as any[];
    }
    
    return [];
  };

  // Local state
  const [unitType, setUnitType] = useState<'incomeUnits' | 'growthUnits'>('growthUnits');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(30);
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc'); // Sort by most recent first

  const flatEntries = getFlattenedEntries();

  // Preserve API order so operation-charge rows remain adjacent to their parent rows.
  const sortedAndFilteredEntries = useMemo(() => {
    // Filter entries by transaction type
    let entries = selectedTransactionTypes.length === 0
      ? flatEntries
      : flatEntries.filter((entry) =>
          selectedTransactionTypes.includes(entry.type)
        );

    entries = sortOrder === 'asc' ? [...entries].reverse() : [...entries];

    return entries;
  }, [flatEntries, selectedTransactionTypes, sortOrder]);

  // Handle sort order change
  const handleSortChange = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  // Get groupId from localStorage
  const getGroupId = () => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
      projectId?: string;
    };
    return selectedProject?.projectId || '';
  };

  // Initial fetch
  useEffect(() => {
    handleFetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitType, resolvedMode]);

  // Fetch expense ledger data
  const handleFetchData = (resetPagination = false, pageToFetch?: number) => {
    const nextPage = resetPagination ? 1 : pageToFetch || currentPage;

    if (resetPagination) {
      dispatch(resetExpenseLedgerPagination());
      setCurrentPage(1);
    }

    dispatch(
      getExpenseLedgerEntries({
        mode: resolvedMode,
        groupId: isInvestorMode ? undefined : getGroupId(),
        serviceId: isInvestorMode ? undefined : 'fundManagementInvestment',
        unitType: isInvestorMode ? undefined : unitType,
        pageSize: rowsPerPage,
        continuationToken: isInvestorMode
          ? null
          : resetPagination
            ? null
            : expenseLedgerContinuationToken,
        pageNumber: isInvestorMode ? nextPage : undefined
      })
    );
  };

  // Handle unit type change
  const handleUnitTypeChange = (event: any) => {
    setUnitType(event.target.value);
    dispatch(clearExpenseLedgerEntries());
  };

  // Handle transaction type filter change
  const handleTransactionTypeChange = (event: any) => {
    const value = event.target.value;
    setSelectedTransactionTypes(typeof value === 'string' ? value.split(',') : value);
  };

  // Handle load more
  const handleLoadMore = () => {
    if (expenseLedgerHasMore && !expenseLedgerLoading) {
      const nextPage = currentPage + 1;
      handleFetchData(false, nextPage);
      setCurrentPage(nextPage);
    }
  };

  // Handle row click to open modal
  const handleRowClick = (entry: any) => {
    setSelectedEntry(entry);
    setModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedEntry(null);
  };

  // Handle refresh
  const handleRefresh = () => {
    dispatch(clearExpenseLedgerEntries());
    handleFetchData(true);
  };

  // Format currency
  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (typeof numAmount !== 'number' || isNaN(numAmount)) return '$0.00';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(numAmount);
  };

  // Format date with proper date and time handling - NOW HANDLES BACKEND NORMALIZED TIMESTAMPS
  const formatDate = (dateString: string | undefined, includeSeconds: boolean = true) => {
    if (!dateString) return 'N/A';
    
    const trimmedDate = String(dateString).trim();
    
    try {
      // If it's only a time string (e.g., "05:30:00" or "5:30"), this is incomplete
      if (/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.test(trimmedDate)) {
        console.warn('⚠️ Incomplete date from API (time only):', dateString);
        return `Time Only: ${trimmedDate} (Date Missing)`;
      }
      
      // Check for midnight pattern that should have been normalized by backend
      if (trimmedDate.includes('T00:00:00')) {
        console.warn('⚠️ Midnight timestamp detected - backend normalization may have failed:', dateString);
      }
      
      // Parse as ISO date or timestamp
      let date: Date;
      
      // Check if it contains both date and time (e.g., "2026-04-09T05:30:00.123Z")
      if (trimmedDate.includes('T') || trimmedDate.includes(' ')) {
        date = new Date(trimmedDate);
      } else if (/^\d{4}-\d{2}-\d{2}/.test(trimmedDate)) {
        // ISO date format
        date = new Date(trimmedDate);
      } else if (/^\d{1,2}\/\d{1,2}\/\d{2,4}/.test(trimmedDate)) {
        // US date format
        date = new Date(trimmedDate);
      } else {
        // Try to parse as-is
        date = new Date(trimmedDate);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('❌ Invalid date format:', dateString);
        return trimmedDate || 'Invalid Date';
      }
      
      // Format with date AND time
      const formatted = date.toLocaleString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: includeSeconds ? '2-digit' : undefined,
        hour12: false
      });
      
      console.log('✅ Formatted timestamp:', { input: dateString, output: formatted });
      return formatted;
    } catch (error) {
      console.warn('❌ Date formatting error:', error, 'Input:', dateString);
      return trimmedDate || 'Invalid Date';
    }
  };
  
  // Helper to show raw timestamp in tooltip (for debugging)
  const getRawTimestampTooltip = (dateString: string | undefined) => {
    if (!dateString) return 'No timestamp';
    const trimmed = String(dateString).trim();
    
    // Check if it's a midnight that wasn't normalized
    if (trimmed.includes('T00:00:00')) {
      return `⚠️ MIDNIGHT (NOT NORMALIZED): ${trimmed}\n\nBackend normalization may have failed. Please check if this entry has createdDateTime or lastUpdatedDateTime.`;
    }
    
    // If it looks properly normalized with actual time
    if (trimmed.includes('T') && !trimmed.includes('T00:00:00')) {
      return `✅ NORMALIZED TIME: ${trimmed}`;
    }
    
    return trimmed;
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (sortedAndFilteredEntries.length === 0) return;

    const headers = [
      'Date',
      'Transaction Type',
      'Description',
      'Amount',
      'Related Asset ID',
      'Investor',
      'Created By'
    ];
    const csvData = sortedAndFilteredEntries.map((entry) => [
      formatDate(entry.date),
      entry.type || 'N/A',
      entry.description || 'No description',
      entry.amount,
      entry.relatedAssetId || 'N/A',
      entry.investorName || entry.relatedInvestorEmail || 'N/A',
      entry.createdBy || 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `expense_ledger_${unitType}_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export to Excel-compatible CSV
  const handleExportExcel = () => {
    if (sortedAndFilteredEntries.length === 0) return;

    const headers = [
      'Date',
      'Expense ID',
      'Transaction Type',
      'Description',
      'Amount (AUD)',
      'Related Asset ID',
      'Investor',
      'Created By',
      'Last Updated'
    ];
    const csvData = sortedAndFilteredEntries.map((entry) => [
      formatDate(entry.date),
      entry.expenseId || entry.id || 'N/A',
      entry.type || 'N/A',
      entry.description || 'No description',
      entry.amount,
      entry.relatedAssetId || 'N/A',
      entry.investorName || entry.relatedInvestorEmail || 'N/A',
      entry.createdBy || 'N/A',
      formatDate(entry.lastUpdatedDateTime || entry.updatedDate || entry.date)
    ]);

    const csvContent =
      '\uFEFF' + // UTF-8 BOM for Excel compatibility
      [
        headers.join(','),
        ...csvData.map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        )
      ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `expense_ledger_${unitType}_${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        padding: { xs: 1, sm: 1.5, md: 2 },
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%)'
      }}
    >
      {/* Header - Reduced size */}
      <Box sx={{ marginBottom: { xs: 1.5, md: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
          <Box
            sx={{
              width: { xs: 28, md: 30 },
              height: { xs: 28, md: 30 },
              borderRadius: 1,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: { xs: 0.8, md: 1 }
            }}
          >
            <FaFileInvoice size={isMobile ? 12 : 14} color="white" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontWeight: 500,
                color: '#333',
                fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' }
              }}
            >
              {isInvestorMode ? 'Investor Ledger Entries' : 'Expense Ledger Entries'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' }
              }}
            >
              {isInvestorMode
                ? 'View your personal ledger transactions across all groups and units'
                : 'View and manage expense transactions for unit types'}
            </Typography>
          </Box>
        </Box>

        {/* Filters and Actions - Smaller buttons */}
        <Grid container spacing={1} sx={{ marginTop: 0.5 }}>
          {!isInvestorMode && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Unit Type</InputLabel>
                  <Select
                    value={unitType}
                    label="Unit Type"
                    onChange={handleUnitTypeChange}
                    sx={{
                      fontSize: '0.75rem',
                      height: '32px',
                      '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px' }
                    }}
                  >
                    <MenuItem value="incomeUnits" sx={{ fontSize: '0.75rem' }}>
                      Income Units
                    </MenuItem>
                    <MenuItem value="growthUnits" sx={{ fontSize: '0.75rem' }}>
                      Growth Units
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Transaction Type</InputLabel>
                  <Select
                    multiple
                    value={selectedTransactionTypes}
                    label="Transaction Type"
                    onChange={handleTransactionTypeChange}
                    sx={{
                      fontSize: '0.75rem',
                      height: '32px',
                      '& .MuiSelect-select': { paddingTop: '6px', paddingBottom: '6px' }
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                      All Types
                    </MenuItem>
                    {Object.entries(TRANSACTION_GROUPS).map(([groupKey, group]) => [
                      <MenuItem
                        key={`${groupKey}-label`}
                        disabled
                        sx={{ fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#f0f0f0' }}
                      >
                        {group.label}
                      </MenuItem>,
                      ...group.types.map((type) => (
                        <MenuItem key={type} value={type} sx={{ fontSize: '0.75rem', pl: 4 }}>
                          {getTransactionTypeConfig(type)?.label || type}
                        </MenuItem>
                      ))
                    ])}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
          <Grid item xs={6} sm={3} md={2.25}>
            <Button
              variant="outlined"
              startIcon={<FaSync size={10} />}
              onClick={handleRefresh}
              disabled={expenseLedgerLoading}
              fullWidth
              sx={{
                height: '32px',
                fontSize: '0.7rem',
                textTransform: 'none',
                padding: '4px 8px'
              }}
            >
              Refresh
            </Button>
          </Grid>
          <Grid item xs={6} sm={3} md={2.25}>
            <Button
              variant="contained"
              startIcon={<FaFileCsv size={10} />}
              onClick={handleExportExcel}
              disabled={expenseLedgerLoading || sortedAndFilteredEntries.length === 0}
              fullWidth
              sx={{
                height: '32px',
                fontSize: '0.7rem',
                textTransform: 'none',
                padding: '4px 8px',
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #3ad06d 0%, #30dfc3 100%)'
                }
              }}
            >
              Export Excel
            </Button>
          </Grid>
          <Grid item xs={12} sm={12} md={2.25}>
            <Button
              variant="contained"
              startIcon={<FaDownload size={10} />}
              onClick={handleExportCSV}
              disabled={expenseLedgerLoading || sortedAndFilteredEntries.length === 0}
              fullWidth
              sx={{
                height: '32px',
                fontSize: '0.7rem',
                textTransform: 'none',
                padding: '4px 8px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)'
                }
              }}
            >
              Export CSV
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Error Display */}
      {expenseLedgerError && (
        <Box sx={{ marginBottom: 1.5 }}>
          <Card sx={{ backgroundColor: '#ffebee', borderLeft: '4px solid #f44336' }}>
            <Box sx={{ padding: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '0.7rem' }} color="error">
                Error: {expenseLedgerError}
              </Typography>
            </Box>
          </Card>
        </Box>
      )}

      {/* Stats Bar - Smaller chips */}
      <Box sx={{ marginBottom: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={`Total: ${sortedAndFilteredEntries.length} entries ${selectedTransactionTypes.length > 0 ? `(filtered)` : ''}`}
          size="small"
          sx={{
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            fontWeight: 500,
            fontSize: '0.65rem',
            height: '20px'
          }}
        />
        {!isInvestorMode && (
          <Chip
            label={`Unit Type: ${unitType === 'incomeUnits' ? 'Income Units' : 'Growth Units'}`}
            size="small"
            sx={{
              backgroundColor: '#f3e5f5',
              color: '#7b1fa2',
              fontWeight: 500,
              fontSize: '0.65rem',
              height: '20px'
            }}
          />
        )}
        {selectedTransactionTypes.length > 0 && (
          <Chip
            label={`Filters: ${selectedTransactionTypes.length} type(s) selected`}
            size="small"
            onDelete={() => setSelectedTransactionTypes([])}
            sx={{
              backgroundColor: '#fff3e0',
              color: '#e65100',
              fontWeight: 500,
              fontSize: '0.65rem',
              height: '20px'
            }}
          />
        )}
      </Box>

      {/* Table - Smaller cells and text */}
      <Card sx={{ borderRadius: 2 }}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '8px' }}>
                  <TableSortLabel
                    active={true}
                    direction={sortOrder}
                    onClick={handleSortChange}
                    sx={{ fontSize: '0.7rem' }}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '8px' }}>
                  Transaction Type
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '8px' }}>
                  Description
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '8px' }}
                >
                  Amount (AUD)
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', padding: '8px' }}>
                  Created By
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenseLedgerLoading && sortedAndFilteredEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                    <Typography
                      variant="body2"
                      sx={{ marginTop: 1, color: '#666', fontSize: '0.7rem' }}
                    >
                      {isInvestorMode ? 'Loading investor entries...' : 'Loading expense entries...'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : sortedAndFilteredEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.7rem' }} color="text.secondary">
                      {selectedTransactionTypes.length > 0
                        ? 'No entries found for selected transaction types'
                        : isInvestorMode
                          ? 'No investor entries found'
                          : 'No expense entries found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedAndFilteredEntries.map((entry, index) => {
                  return (
                    <TableRow
                      key={entry.expenseId || entry.id || index}
                      hover
                      onClick={() => handleRowClick(entry)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#f0f0f0'
                        }
                      }}
                    >
                      <TableCell sx={{ fontSize: '0.68rem', padding: '8px' }}>
                        <Tooltip title={getRawTimestampTooltip(entry.date)} arrow placement="top">
                          <span>{formatDate(entry.date)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.68rem', padding: '8px' }}>
                        <TransactionTypeBadge type={entry.type} size="small" />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.68rem', padding: '8px', maxWidth: 300 }}>
                        {entry.description || 'No description'}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontSize: '0.68rem', fontWeight: 500, padding: '8px' }}
                      >
                        {formatCurrency(entry.amount)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.68rem', padding: '8px' }}>
                        {entry.createdBy || 'N/A'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Load More Button - Smaller */}
        {expenseLedgerHasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleLoadMore}
              disabled={expenseLedgerLoading}
              startIcon={expenseLedgerLoading ? <CircularProgress size={14} /> : null}
              sx={{ fontSize: '0.7rem', textTransform: 'none', padding: '4px 12px' }}
            >
              {expenseLedgerLoading ? 'Loading...' : 'Load More'}
            </Button>
          </Box>
        )}
      </Card>

      {/* User Details Modal */}
      <UserDetailsModal open={modalOpen} entry={selectedEntry} onClose={handleModalClose} />
    </Container>
  );
};

export default ExpenseLedger;
